import type {
  Duration, Label, Onset,
} from '@/data';
import {
  BLC, Block, Line, Cell,
} from '@/data';
import type { Image3DRowFormat, Image3DSyncFormat } from '@/formats/UnitParser';

export function createCell(label: Label, nextOnset: Onset): Cell {
  const { onset } = label;
  const duration = (nextOnset - onset) as Duration;
  return new Cell(duration, onset);
}

export function createCells(labels: Label[], pieceOnsetLength: Onset): Cell[] {
  return labels.map((l: Label, index, arrayLabels) => {
    const nextOnset: Onset = arrayLabels[index + 1]?.onset || pieceOnsetLength;
    return createCell(l, nextOnset);
  });
}

function generateOnsetX(cell: Cell) {
  const endOfCell = cell.onset + cell.duration;
  return {
    onset: endOfCell,
    x: (endOfCell) * BLC.pixelByOnset,
  };
}

function addFirstOnsetX(onsetXList: { onset: number; x: number; }[], firstOnsetForTheRow: number) {
  onsetXList.unshift({
    onset: firstOnsetForTheRow,
    x: firstOnsetForTheRow * BLC.pixelByOnset,
  });
}

function alignOnsetXToTheLeft(onsetX: { onset: number; x: number; }, firstOnsetForTheRow: number) {
  return {
    onset: onsetX.onset,
    x: onsetX.x - firstOnsetForTheRow * BLC.pixelByOnset,
  };
}

function generateOnsetXList(line: Line) {
  const firstOnsetForTheRow = line.cells[0].onset;

  const onsetXList = line.cells.map(generateOnsetX);

  addFirstOnsetX(onsetXList, firstOnsetForTheRow);

  const alignedOnsetXList = onsetXList
    .map((onsetX) => alignOnsetXToTheLeft(onsetX, firstOnsetForTheRow));

  return alignedOnsetXList;
}

function generateRow(line: Line, rowIndex: number): Image3DRowFormat {
  const onsetXList = generateOnsetXList(line);

  const row: Image3DRowFormat = {
    'onset-x': onsetXList,
    height: BLC.rowHeight,
    y: rowIndex * BLC.rowHeight,
    'start-x': 0,
    'end-x': line.getDuration() * BLC.pixelByOnset,
  };
  return row;
}

function generateRows(lines: Line[]) {
  return lines.map((line, rowIndex) => generateRow(line, rowIndex));
}

function generateRowsWithMargin(block: Block, blockVerticalMargin: number) {
  const rows: Image3DRowFormat[] = generateRows(block.lines).map((row) => ({
    ...row,
    y: row.y + blockVerticalMargin,
  }));

  return rows;
}

function sumPreviousBlocksHeight(blocksArray: Block[], blockIndex: number) {
  const sumBlocksHeight = blocksArray
    .filter((b, i) => i <= blockIndex)
    .map((b) => b.getHeight())
    .reduce((p, c) => p + c);
  const lastHeight = (blocksArray?.at(blockIndex)
    ?.getHeight() || 0);
  const blockVerticalMargin = sumBlocksHeight - lastHeight;
  return blockVerticalMargin;
}

export function generatePositions(blocks: Block[]): Image3DSyncFormat {
  const rows = blocks.flatMap((block, blockIndex, blocksArray) => {
    const blockVerticalMargin = sumPreviousBlocksHeight(blocksArray, blockIndex);
    return generateRowsWithMargin(block, blockVerticalMargin);
  });

  const page = { rows, image: 'undefined' };
  return { pages: [page] } as Image3DSyncFormat;
}

export function getGeneratedPositions(): Image3DSyncFormat {
  const block1 = new Block([
    new Line([
      new Cell(4, 0),
    ]),
    new Line([
      new Cell(4, 4),
      new Cell(2, 8),
      new Cell(4, 10),
    ]),
    new Line([
      new Cell(2, 14),
      new Cell(2, 16),
    ]),
  ]);

  const block2 = new Block([
    new Line([
      new Cell(2, 18),
      new Cell(4, 20),
    ]),
    new Line([
      new Cell(2, 24),
      new Cell(3, 26),
    ]),
  ]);

  const block3 = new Block([
    new Line([
      new Cell(3, 29),
    ]),
    new Line([
      new Cell(4, 32),
      new Cell(5, 36),
    ]),
    new Line([
      new Cell(4, 41),
      new Cell(3, 45),
    ]),
  ]);

  return generatePositions([block1, block2, block3]);
}
